import { useEffect, useState, lazy } from "react";
import { useNavigate } from "react-router-dom";
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

import useFetch from "hooks/useFetch";

import { GET, POST } from 'api/api';
import LottoResults from "components/lotto/results/LottoResults";
import ContactUs from "components/lotto/default/ContactUs";
import OtherApps from "components/lotto/default/OtherApps";
import DirectAdsComponent from "components/lotto/default/DirectAdsComponent";
import DreamSearchForm from "components/lotto/default/DreamSearchForm";
import QuickPrompts from "components/lotto/default/QuickPrompts";
import StickySearchBar from "components/lotto/default/StickySearchBar";
import RecentSearches from "components/lotto/default/RecentSearches";
import ViralShareCard from "components/viral/ViralShareCard";
import ViralInviteBanner from "components/viral/ViralInviteBanner";
import ShareStreakBadge from "components/viral/ShareStreakBadge";
import ReferralWelcome from "components/viral/ReferralWelcome";
import SavedNumbers from "components/lotto/default/SavedNumbers";
import SaveNumberButton from "components/lotto/default/SaveNumberButton";
import WelcomeBanner from "components/lotto/default/WelcomeBanner";
import DrawCountdownHero from "components/lotto/default/DrawCountdownHero";
import OnboardingTip from "components/lotto/default/OnboardingTip";
import TarotTeaser from "components/tarot/TarotTeaser";
import AiReferenceFaq from "components/seo/AiReferenceFaq";
import SeoInternalLinks from "components/seo/SeoInternalLinks";
import LazySection from "components/common/LazySection";
import EmptyState from "components/common/EmptyState";
import { getSearchMode } from 'seo/seoConfig';
import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  getSavedNumbers,
  toggleSavedNumber,
  removeSavedNumber,
  isNumberSaved,
  isOnboardingDone,
  setOnboardingDone,
} from 'storage/userStorage';

import { PrizeCollapsible } from 'components/lotto/utility/PrizeCollapsible';
import { HintCollapsible } from 'components/lotto/utility/HintCollapsible';
import { ResultCollapsible } from 'components/lotto/utility/ResultCollapsible';

import { format } from 'date-fns';

import "css/input-box.css";
import "css/affiliate.css";
import "css/tarot.css";

const ArticleList = lazy(() => import('components/lotto/ArticleList'));

export default function Lotto({ isShowAtRender, keyword, visitInfo }) {
  const navigate = useNavigate();
  const { data, setData, isLoading } = useFetch();
  const [draftSlug, setDraftSlug] = useState(keyword || '');
  const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());
  const [savedNumbers, setSavedNumbers] = useState(() => getSavedNumbers());
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboardingDone());
  const searchMode = getSearchMode(keyword);

  localStorage.setItem('isShowAtRender', isShowAtRender);

  useEffect(() => {
    if (keyword) {
      setData((prev) => ({ ...prev, slug: keyword }));
      setDraftSlug(keyword);
    }
  }, [keyword, setData]);

  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, [keyword]);

  const [hintAndPrizeData, setHintAndPrizeData] = useState(null);
  useEffect(() => {
    const getHintAndPrizeData = async () => {
      try {
        const hintAndPrizeData = await GET('/lotto/hint?q=', {});
        setHintAndPrizeData(hintAndPrizeData);
      } catch (error) {
        console.error('/lotto/hint --> error: ', error);
      }
    };
    getHintAndPrizeData();
  }, []);

  const [topListDataResponse, setTopListDataResponse] = useState(null);
  useEffect(() => {
    const getTopListDataResponse = async () => {
      try {
        const topListDataResponse = await POST('/lotto/get-top-list', {});
        setTopListDataResponse(topListDataResponse);
      } catch (error) {
        console.error('/lotto/get-top-list --> error: ', error);
      }
    };
    getTopListDataResponse();
  }, []);

  const [topList1YDataResponse, setTopList1YDataResponse] = useState(null);
  useEffect(() => {
    const getTopList1YDataResponse = async () => {
      try {
        var beg = new Date();
        beg.setFullYear(beg.getFullYear() - 1);
        beg = format(beg, 'yyyy-MM-dd');
        var end = new Date();
        end = format(end, 'yyyy-MM-dd');
        const topList1YDataResponse = await POST('/lotto/get-top-list', { begDate: beg, endDate: end });
        setTopList1YDataResponse(topList1YDataResponse);
      } catch (error) {
        console.error('/lotto/get-top-list --> error: ', error);
      }
    };
    getTopList1YDataResponse();
  }, []);

  const [topList3YDataResponse, setTopList3YDataResponse] = useState(null);
  useEffect(() => {
    const getTopList3YDataResponse = async () => {
      try {
        var beg = new Date();
        beg.setFullYear(beg.getFullYear() - 3);
        beg = format(beg, 'yyyy-MM-dd');
        var end = new Date();
        end = format(end, 'yyyy-MM-dd');
        const topList3YDataResponse = await POST('/lotto/get-top-list', { begDate: beg, endDate: end });
        setTopList3YDataResponse(topList3YDataResponse);
      } catch (error) {
        console.error('/lotto/get-top-list --> error: ', error);
      }
    };
    getTopList3YDataResponse();
  }, []);

  const activeSlug = keyword || data.slug;

  const handleSearch = (value) => {
    const trimmed = (value ?? draftSlug).trim();
    if (!trimmed) return;
    setRecentSearches(addRecentSearch(trimmed));
    navigate(`/search/${encodeURIComponent(trimmed)}`);
  };

  const handleToggleSaveNumber = (number) => {
    const result = toggleSavedNumber(number, number);
    setSavedNumbers(result.items);
  };

  const scrollToHints = () => {
    document.getElementById('hints')?.scrollIntoView({ behavior: 'smooth' });
  };

  const dismissOnboarding = () => {
    setOnboardingDone();
    setShowOnboarding(false);
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className="skeleton-results" role="status" aria-live="polite">
          <div className="skeleton-line skeleton-line--lg" />
          <div className="skeleton-line" />
          <div className="skeleton-line skeleton-line--sm" />
        </div>
      );
    }

    if (data.results?.statusCode === 200) {
      return (
        <section id="results" className="results-section">
          <div className="results-toolbar">
            <ViralShareCard keyword={keyword} mode={searchMode} variant="result" />
            <SaveNumberButton
              number={keyword}
              isSaved={isNumberSaved(keyword)}
              onToggle={handleToggleSaveNumber}
            />
          </div>
          <LottoResults data={data} isShowAtRender={isShowAtRender} />
        </section>
      );
    }

    if (data.results?.statusCode === 404) {
      return (
        <EmptyState
          icon="✏️"
          title="พิมพ์ความฝันให้ชัดขึ้น"
          message="ลองอธิบายความฝันให้ละเอียดขึ้น เช่น ฝันเห็นงูใหญ่สีดำ หรือ ฝันเห็นพระสงฆ์"
        />
      );
    }

    if (!keyword && !activeSlug) {
      return (
        <EmptyState
          icon="💫"
          title="เริ่มค้นหาเลขเด็ด"
          message="พิมพ์ความฝันหรือเลขที่สนใจ แล้วกดค้นหาเลขเด็ด"
        />
      );
    }

    return null;
  };

  return (
    <main id="main-content" className="lotto-main">
      <StickySearchBar
        visible={!!keyword}
        value={draftSlug}
        onChange={(e) => setDraftSlug(e.target.value)}
        onSubmit={() => handleSearch()}
        isLoading={isLoading}
      />

      {!keyword && <ReferralWelcome />}

      {!keyword && showOnboarding && (
        <OnboardingTip onDismiss={dismissOnboarding} />
      )}

      {!keyword && visitInfo?.isReturning && (
        <WelcomeBanner isReturning={visitInfo.isReturning} streak={visitInfo.streak} />
      )}

      {!keyword && visitInfo?.streak >= 2 && (
        <ShareStreakBadge streak={visitInfo.streak} />
      )}

      {!keyword && <ViralInviteBanner />}

      {!keyword && (
        <DrawCountdownHero
          nextLottoTimestamp={hintAndPrizeData?.hintData?.[0]?.lottoDate}
          onSearchHints={scrollToHints}
        />
      )}

      {!keyword && <TarotTeaser />}

      <DreamSearchForm
        variant={keyword ? 'compact' : 'home'}
        value={draftSlug}
        onChange={(e) => setDraftSlug(e.target.value)}
        onSubmit={() => handleSearch()}
        isLoading={isLoading}
      />

      {!keyword && (
        <RecentSearches
          items={recentSearches}
          onSelect={(v) => handleSearch(v)}
          onClear={() => {
            clearRecentSearches();
            setRecentSearches([]);
          }}
        />
      )}

      {!keyword && (
        <SavedNumbers
          items={savedNumbers}
          onSelect={(v) => handleSearch(v)}
          onRemove={(num) => setSavedNumbers(removeSavedNumber(num))}
        />
      )}

      {!keyword && (
        <QuickPrompts onSelect={(v) => handleSearch(v)} />
      )}

      {renderResults()}

      <section id="hints" className="content-section">
        <HintCollapsible
          topic="เลขเด็ดงวดนี้"
          hintDataArray={hintAndPrizeData?.hintData}
          hintInsightDataArray={hintAndPrizeData?.hintInsightData}
          slug={activeSlug}
          isShowAtRender={isShowAtRender}
        />
      </section>

      <section id="prizes" className="content-section">
        <PrizeCollapsible
          topic="ผลรางวัลที่หนึ่ง/สองตัวล่าง"
          prizeDataArray={hintAndPrizeData?.prizeData}
          slug={activeSlug}
          isShowAtRender={isShowAtRender}
        />
      </section>

      <section id="articles" className="content-section">
        <LazySection label="กำลังโหลดบทความ...">
          <ArticleList />
        </LazySection>
      </section>

      <section id="stats" className="content-section">
        <LazySection label="กำลังโหลดสถิติ...">
          <ResultCollapsible
            id="3"
            topic="เลขถูกบ่อย สถิติย้อนหลัง 1 ปี"
            table={topList1YDataResponse}
            slug={activeSlug}
            isShowAtRender={false}
          />
          <ResultCollapsible
            id="3"
            topic="เลขถูกบ่อย สถิติย้อนหลัง 3 ปี"
            table={topList3YDataResponse}
            slug={activeSlug}
            isShowAtRender={false}
          />
          <ResultCollapsible
            id="3"
            topic={`เลขถูกบ่อย สถิติย้อนหลัง ${new Date().getFullYear() - 1990} ปี`}
            table={topListDataResponse}
            slug={activeSlug}
            isShowAtRender={false}
          />
          <ResultCollapsible
            id="9"
            topic="ความฝันยอดฮิต"
            table={hintAndPrizeData?.dreamData}
            slug={activeSlug}
            isShowAtRender={isShowAtRender}
          />
        </LazySection>
      </section>

      {!keyword && (
        <section id="seo-links" className="content-section">
          <SeoInternalLinks />
        </section>
      )}

      {!keyword && (
        <section id="faq" className="content-section">
          <AiReferenceFaq />
        </section>
      )}

      <section className="content-section content-section--muted">
        <OtherApps nextLottoTimestamp={hintAndPrizeData?.hintData?.[0]?.lottoDate} />
        <DirectAdsComponent adsType="lazada_bonus_deeplink" />
        <ContactUs />
      </section>
    </main>
  );
}
